/**
 * Custom sprite demo page
 * Renders custom Aseprite sprites using the TileMapRenderer
 * No dependencies on src/qud - demonstrates standalone usage
 */

import { WebGLContext } from '@/graphics/WebGLContext';
import { TileMapShader } from '@/graphics/TileMapShader';
import { TileMapRenderer } from '@/graphics/TileMapRenderer';
import { FontSpriteIndex } from '@/fonts/types';
import { toCP437 } from '@/fonts/cp437';

// Aseprite sprite sizes
const SPRITE_CELL_WIDTH = 16;
const SPRITE_CELL_HEIGHT = 24;

// DOS font sizes
const FONT_CELL_WIDTH = 9;
const FONT_CELL_HEIGHT = 16;
const TERMINAL_COLS = 80;
const TERMINAL_ROWS = 25;

interface SpriteEntry {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface SpriteIndex {
  atlas: string;
  atlasWidth: number;
  atlasHeight: number;
  sprites: Record<string, SpriteEntry>;
}

export async function renderSpriteDemo(appElement: HTMLDivElement): Promise<void> {
  // Load sprite index first to calculate layout
  const indexResponse = await fetch('/data/custom-sprites/index.json');
  if (!indexResponse.ok) {
    appElement.innerHTML = `
      <div style="padding: 20px; font-family: monospace;">
        <h2>Custom Sprite Demo</h2>
        <p>Custom sprites not found. Run ./dev-cli app build first.</p>
      </div>
    `;
    return;
  }
  const index: SpriteIndex = await indexResponse.json();
  const spriteNames = Object.keys(index.sprites);

  if (spriteNames.length === 0) {
    appElement.innerHTML = `
      <div style="padding: 20px; font-family: monospace;">
        <h2>Custom Sprite Demo</h2>
        <p>No sprites in atlas. Add .aseprite files to assets/sprites/ and run ./dev-cli app build.</p>
      </div>
    `;
    return;
  }

  // Calculate sprite grid layout
  const cols = Math.ceil(Math.sqrt(spriteNames.length));
  const rows = Math.ceil(spriteNames.length / cols);
  const spriteScale = 4;
  const spriteContainerWidth = cols * SPRITE_CELL_WIDTH * spriteScale;
  const spriteContainerHeight = rows * SPRITE_CELL_HEIGHT * spriteScale;

  // Terminal size
  const termScale = 2;
  const termContainerWidth = TERMINAL_COLS * FONT_CELL_WIDTH * termScale;
  const termContainerHeight = TERMINAL_ROWS * FONT_CELL_HEIGHT * termScale;

  // Setup HTML - note: canvas uses 100% size, container is fixed
  appElement.innerHTML = `
    <div style="padding: 20px; font-family: monospace; overflow-y: auto; height: 100%;">
      <h2>Custom Sprite Demo</h2>
      <div id="canvas-container" style="width: ${spriteContainerWidth}px; height: ${spriteContainerHeight}px; border: 1px solid #333; position: relative;">
        <canvas id="demo-canvas" style="width: 100%; height: 100%; display: block;"></canvas>
      </div>
      <div id="status" style="margin-top: 10px;">Loading sprites...</div>
      <div id="controls" style="margin-top: 10px;">
        <button id="randomize">Randomize Colors</button>
      </div>

      <h2 style="margin-top: 30px;">DOS Font Terminal (80x25)</h2>
      <div id="terminal-container" style="width: ${termContainerWidth}px; height: ${termContainerHeight}px; border: 1px solid #333; background: #000; position: relative;">
        <canvas id="terminal-canvas" style="width: 100%; height: 100%; display: block;"></canvas>
      </div>
      <div id="terminal-status" style="margin-top: 10px;">Loading font...</div>
    </div>
  `;

  const canvas = document.getElementById('demo-canvas') as HTMLCanvasElement;
  const statusEl = document.getElementById('status') as HTMLDivElement;
  const randomizeBtn = document.getElementById('randomize') as HTMLButtonElement;
  const terminalCanvas = document.getElementById('terminal-canvas') as HTMLCanvasElement;
  const terminalStatusEl = document.getElementById('terminal-status') as HTMLDivElement;

  try {
    // Load atlas image
    const atlasImage = await loadImage('/data/custom-sprites/atlas.png');

    // Create renderer - WebGLContext will read parent size and handle DPR
    const ctx = new WebGLContext(canvas);
    const shader = new TileMapShader(ctx.gl);
    const renderer = new TileMapRenderer(ctx, shader, SPRITE_CELL_WIDTH, SPRITE_CELL_HEIGHT);

    // Set view: scale factor, no offset needed
    renderer.setView(0, 0, spriteScale);

    // Load sprites into the atlas
    const allocator = renderer.getAtlasAllocator();
    const spriteIndices: Map<string, number> = new Map();

    // Get atlas pixel data once
    const atlasCanvas = document.createElement('canvas');
    atlasCanvas.width = atlasImage.width;
    atlasCanvas.height = atlasImage.height;
    const atlasCtx = atlasCanvas.getContext('2d')!;
    atlasCtx.drawImage(atlasImage, 0, 0);
    const atlasData = atlasCtx.getImageData(0, 0, atlasImage.width, atlasImage.height);

    for (const name of spriteNames) {
      const entry = index.sprites[name];
      // Extract sprite region as Uint8Array (Y-flipped for GL since FLIP_Y doesn't work with ArrayBufferView)
      const spriteData = new Uint8Array(entry.width * entry.height * 4);
      for (let y = 0; y < entry.height; y++) {
        const srcY = entry.y + (entry.height - 1 - y);
        const srcOffset = (srcY * atlasImage.width + entry.x) * 4;
        const dstOffset = y * entry.width * 4;
        spriteData.set(atlasData.data.subarray(srcOffset, srcOffset + entry.width * 4), dstOffset);
      }
      const cellIndex = allocator.allocateAndUpload(spriteData);
      spriteIndices.set(name, cellIndex);
    }

    // Generate random colors for each sprite
    let spriteColors: Map<string, { fg: number[]; detail: number[] }> = new Map();

    function randomizeColors() {
      spriteColors = new Map();
      for (const name of spriteNames) {
        spriteColors.set(name, {
          fg: [Math.random(), Math.random(), Math.random()],
          detail: [Math.random(), Math.random(), Math.random()],
        });
      }
    }

    function render() {
      ctx.bindDefaultFramebuffer();
      ctx.clear(0.1, 0.1, 0.15, 1.0);

      renderer.setVisibleTileRange(0, 0, cols, rows);
      renderer.beginFrame();

      let i = 0;
      for (const name of spriteNames) {
        const cellIndex = spriteIndices.get(name)!;
        const colors = spriteColors.get(name)!;
        const col = i % cols;
        const row = Math.floor(i / cols);

        renderer.addInstance(
          col,
          row,
          cellIndex,
          colors.fg[0],
          colors.fg[1],
          colors.fg[2],
          1.0,
          colors.detail[0],
          colors.detail[1],
          colors.detail[2],
          1.0
        );
        i++;
      }

      renderer.endFrame();
    }

    // Initial render
    randomizeColors();
    render();

    // Randomize button
    randomizeBtn.addEventListener('click', () => {
      randomizeColors();
      render();
    });

    statusEl.textContent = `Loaded ${spriteNames.length} sprites: ${spriteNames.join(', ')}`;

    // === Terminal Demo ===
    await renderTerminalDemo(terminalCanvas, terminalStatusEl, termScale);
  } catch (error) {
    statusEl.textContent = `Error: ${error instanceof Error ? error.message : String(error)}`;
    console.error(error);
  }
}

/**
 * Render the terminal demo with DOS font
 */
async function renderTerminalDemo(
  canvas: HTMLCanvasElement,
  statusEl: HTMLDivElement,
  scale: number
): Promise<void> {
  try {
    // Load font index
    const fontResponse = await fetch('/data/fonts/bm437-ibm-vga-9x16.json');
    if (!fontResponse.ok) {
      statusEl.textContent = 'Font not found. Run ./dev-cli app build first.';
      return;
    }
    const fontIndex: FontSpriteIndex = await fontResponse.json();

    // Load font atlas
    const fontImage = await loadImage('/data/fonts/bm437-ibm-vga-9x16.png');

    // Create terminal renderer
    const ctx = new WebGLContext(canvas);
    const shader = new TileMapShader(ctx.gl);
    const renderer = new TileMapRenderer(ctx, shader, FONT_CELL_WIDTH, FONT_CELL_HEIGHT);
    renderer.setView(0, 0, scale);

    // Load all font glyphs into atlas
    const allocator = renderer.getAtlasAllocator();
    const charIndices: Map<number, number> = new Map();

    // Get font atlas pixel data
    const fontCanvas = document.createElement('canvas');
    fontCanvas.width = fontImage.width;
    fontCanvas.height = fontImage.height;
    const fontCtx = fontCanvas.getContext('2d')!;
    fontCtx.drawImage(fontImage, 0, 0);
    const fontData = fontCtx.getImageData(0, 0, fontImage.width, fontImage.height);

    // Upload each character glyph
    for (let charCode = fontIndex.firstChar; charCode <= fontIndex.lastChar; charCode++) {
      const charPos = fontIndex.chars[charCode];
      if (!charPos) continue;

      // Extract glyph region (Y-flipped for GL)
      const glyphData = new Uint8Array(fontIndex.width * fontIndex.height * 4);
      for (let y = 0; y < fontIndex.height; y++) {
        const srcY = charPos.y + (fontIndex.height - 1 - y);
        for (let x = 0; x < fontIndex.width; x++) {
          // Browser converts grayscale PNG to RGBA when drawn on canvas
          const srcIdx = (srcY * fontImage.width + charPos.x + x) * 4;
          const dstIdx = (y * fontIndex.width + x) * 4;
          // R channel contains the grayscale value
          const gray = fontData.data[srcIdx];
          glyphData[dstIdx + 0] = gray; // R
          glyphData[dstIdx + 1] = gray; // G
          glyphData[dstIdx + 2] = gray; // B
          glyphData[dstIdx + 3] = 255; // A
        }
      }

      const cellIndex = allocator.allocateAndUpload(glyphData);
      charIndices.set(charCode, cellIndex);
    }

    // Demo text to display
    const demoText = [
      '╔══════════════════════════════════════════════════════════════════════════════╗',
      '║                     PROJECT NAVIGATOR - DOS Font Demo                        ║',
      '╠══════════════════════════════════════════════════════════════════════════════╣',
      '║                                                                              ║',
      '║  This is a classic IBM VGA 9x16 bitmap font rendered using WebGL.            ║',
      '║                                                                              ║',
      '║  The font supports all 256 characters from CP437:                            ║',
      '║                                                                              ║',
      '║  Box drawing: ┌─┐│└┘├┤┬┴┼ ║═╔╗╚╝╠╣╦╩╬                                        ║',
      '║  Blocks: ░▒▓█ ▀▄▌▐                                                           ║',
      '║  Arrows: ←↑→↓↔↕                                                              ║',
      '║  Math: ±×÷≤≥≠≈∞√∫                                                            ║',
      '║  Greek: αβγδεζηθικλμνξπρστυφχψω                                              ║',
      '║  Misc: ☺☻♥♦♣♠•◘○◙♂♀♪♫☼►◄▲▼                                                   ║',
      '║                                                                              ║',
      '║  ABCDEFGHIJKLMNOPQRSTUVWXYZ 0123456789                                       ║',
      '║  abcdefghijklmnopqrstuvwxyz !@#$%^&*()                                       ║',
      '║                                                                              ║',
      '║  "Live as if you were to die tomorrow.                                       ║',
      '║   Learn as if you were to live forever."  -- Mahatma Gandhi                  ║',
      '║                                                                              ║',
      '╠══════════════════════════════════════════════════════════════════════════════╣',
      '║  80 columns x 25 rows = 2000 characters   │   Rendered at 2x scale           ║',
      '╚══════════════════════════════════════════════════════════════════════════════╝',
      '                                                                                ',
    ];

    // CGA colors
    const cgaColors: [number, number, number][] = [
      [0, 0, 0], // 0: Black
      [0, 0, 170], // 1: Blue
      [0, 170, 0], // 2: Green
      [0, 170, 170], // 3: Cyan
      [170, 0, 0], // 4: Red
      [170, 0, 170], // 5: Magenta
      [170, 85, 0], // 6: Brown
      [170, 170, 170], // 7: Light Gray
      [85, 85, 85], // 8: Dark Gray
      [85, 85, 255], // 9: Light Blue
      [85, 255, 85], // 10: Light Green
      [85, 255, 255], // 11: Light Cyan
      [255, 85, 85], // 12: Light Red
      [255, 85, 255], // 13: Light Magenta
      [255, 255, 85], // 14: Yellow
      [255, 255, 255], // 15: White
    ];

    function renderTerminal() {
      ctx.bindDefaultFramebuffer();
      ctx.clear(0, 0, 0, 1.0);

      renderer.setVisibleTileRange(0, 0, TERMINAL_COLS, TERMINAL_ROWS);
      renderer.beginFrame();

      for (let row = 0; row < TERMINAL_ROWS; row++) {
        const line = demoText[row] || '';
        for (let col = 0; col < TERMINAL_COLS; col++) {
          const unicodeCode = col < line.length ? line.charCodeAt(col) : 32;
          // Convert Unicode to CP437 for box drawing and special chars
          const charCode = toCP437(unicodeCode);
          const cellIndex = charIndices.get(charCode) ?? charIndices.get(32)!;

          // Use yellow text for title, cyan for frame border, gray for content
          let fg = cgaColors[7]; // Default: light gray
          if (row === 1) {
            fg = cgaColors[14]; // Yellow for title
          } else if ((col === 0 || col === 79) && line[col] && '╔╗╚╝╠╣║'.includes(line[col])) {
            // Only color the left/right frame edges cyan
            fg = cgaColors[11]; // Light cyan for frame
          } else if (
            (row === 0 || row === 2 || row === 21 || row === 23) &&
            line[col] &&
            '╔╗╚╝╠╣╦╩╬═'.includes(line[col])
          ) {
            // Color the top/bottom frame lines cyan
            fg = cgaColors[11]; // Light cyan for frame
          }

          renderer.addInstance(
            col,
            row,
            cellIndex,
            fg[0] / 255,
            fg[1] / 255,
            fg[2] / 255,
            1.0,
            (fg[0] / 255) * 0.5,
            (fg[1] / 255) * 0.5,
            (fg[2] / 255) * 0.5,
            1.0
          );
        }
      }

      renderer.endFrame();
    }

    renderTerminal();
    statusEl.textContent = `Loaded ${fontIndex.name} font (${fontIndex.width}x${fontIndex.height})`;
  } catch (error) {
    statusEl.textContent = `Error: ${error instanceof Error ? error.message : String(error)}`;
    console.error(error);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}
